import {
  type ChangeRuleTypeMetadata,
  Partner,
  useChangeRuleTypes,
} from '@/api/hooks/useTenantConfig'
import { Alert, Modal, Spin, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { ChangeRuleSlider } from './ChangeRuleSlider'
import { ZONE_COLORS } from './constants'
import type {
  ChangeCategory,
  OrderChangeConfigInput,
  UntilStage,
} from './types'

const { Text, Paragraph } = Typography

interface LegendItemProps {
  color: string
  label: string
}

function LegendItem({ color, label }: LegendItemProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Text type='secondary'>{label}</Text>
    </div>
  )
}

interface ChangeManagementConfigModalProps {
  open: boolean
  onClose: () => void
  partner: Partner | null
  onSave: (config: OrderChangeConfigInput) => void
  loading?: boolean
  // When true, the connected local instance is an external (non-S&D) system. Order
  // changes can never be auto-accepted in that case (enforced server-side), so the
  // auto-accept handles are locked at the earliest stage and grayed out.
  autoAcceptDisabled?: boolean
}

// Maps API keys to frontend category names for icons
const KEY_TO_CATEGORY: Record<string, ChangeCategory> = {
  supply_change_rules: 'supply',
  dest_change_rule: 'destination',
  delivery_window_change_rules: 'delivery_window',
}

interface RuleSliderState {
  autoAcceptUntil: string
  manualApprovalUntil: string
}

type SliderState = Record<string, RuleSliderState>

function configToSliderState(
  config: OrderChangeConfigInput | undefined,
  ruleTypes: ChangeRuleTypeMetadata[],
): SliderState {
  const state: SliderState = {}
  for (const ruleType of ruleTypes) {
    const configValue = config?.[ruleType.key as keyof OrderChangeConfigInput]
    state[ruleType.key] = {
      autoAcceptUntil:
        (configValue as { auto_until?: string } | undefined)?.auto_until ||
        ruleType.default_until,
      manualApprovalUntil:
        (configValue as { manual_until?: string } | undefined)?.manual_until ||
        ruleType.default_until,
    }
  }
  return state
}

function sliderStateToConfig(
  state: SliderState,
  ruleTypes: ChangeRuleTypeMetadata[],
): OrderChangeConfigInput {
  const config: Record<string, { auto_until: string; manual_until: string }> =
    {}
  for (const ruleType of ruleTypes) {
    const ruleState = state[ruleType.key]
    if (ruleState) {
      config[ruleType.key] = {
        auto_until: ruleState.autoAcceptUntil,
        manual_until: ruleState.manualApprovalUntil,
      }
    }
  }
  return config as OrderChangeConfigInput
}

export function ChangeManagementConfigModal({
  open,
  onClose,
  partner,
  onSave,
  loading,
  autoAcceptDisabled = false,
}: ChangeManagementConfigModalProps) {
  const { data: ruleTypes, isLoading: isLoadingRuleTypes } =
    useChangeRuleTypes()
  const [sliderState, setSliderState] = useState<SliderState>({})

  const initialState = useMemo(() => {
    if (!ruleTypes) return {}
    const state = configToSliderState(
      partner?.change_management_config,
      ruleTypes,
    )
    // Mirror the server-side rule: an external connected instance can never auto-accept,
    // so pin every auto-accept handle to the earliest stage.
    if (autoAcceptDisabled) {
      for (const ruleType of ruleTypes) {
        const ruleState = state[ruleType.key]
        const earliestStage = ruleType.available_until_states[0]?.label
        if (ruleState && earliestStage) {
          ruleState.autoAcceptUntil = earliestStage
        }
      }
    }
    return state
  }, [partner?.change_management_config, ruleTypes, autoAcceptDisabled])

  // Compute union of all stages across all rule types, ordered by the fullest rule
  // type first. That rule (e.g. _UntilComplete) is the complete progression, so its
  // order is canonical; seeding from a shorter rule would misplace stages it omits
  // (e.g. "After Complete" landing before "Dropping"/"Complete").
  const masterStages = useMemo(() => {
    if (!ruleTypes) return []
    const orderedRuleTypes = [...ruleTypes].sort(
      (a, b) =>
        b.available_until_states.length - a.available_until_states.length,
    )
    const allStages: string[] = []
    for (const ruleType of orderedRuleTypes) {
      for (const stage of ruleType.available_until_states) {
        if (!allStages.includes(stage.label)) {
          allStages.push(stage.label)
        }
      }
    }
    return allStages
  }, [ruleTypes])

  useEffect(() => {
    if (open && ruleTypes) {
      setSliderState(initialState)
    }
  }, [open, ruleTypes, initialState])

  const handleSave = () => {
    if (!ruleTypes) return
    onSave(sliderStateToConfig(sliderState, ruleTypes))
  }

  const handleAutoAcceptChange = (key: string, stage: UntilStage) => {
    setSliderState((prev) => {
      const currentRule = prev[key]
      if (!currentRule) return prev
      return {
        ...prev,
        [key]: {
          ...currentRule,
          autoAcceptUntil: stage as string,
        },
      }
    })
  }

  const handleManualApprovalChange = (key: string, stage: UntilStage) => {
    setSliderState((prev) => {
      const currentRule = prev[key]
      if (!currentRule) return prev
      return {
        ...prev,
        [key]: {
          ...currentRule,
          manualApprovalUntil: stage as string,
        },
      }
    })
  }

  return (
    <Modal
      title='Change Management Configuration'
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText='Save Configuration'
      confirmLoading={loading}
      width={760}
    >
      <Paragraph type='secondary' style={{ paddingBottom: 24 }}>
        Configure how order changes are handled at different stages of the order
        lifecycle.
      </Paragraph>

      {autoAcceptDisabled && (
        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
          message='Because your system is not Gravitate S&D, order changes must be manually approved. You can still configure when order changes should be automatically rejected.'
        />
      )}

      {isLoadingRuleTypes ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : (
        ruleTypes?.map((ruleType) => {
          const ruleState = sliderState[ruleType.key]
          if (!ruleState) return null

          const category = KEY_TO_CATEGORY[ruleType.key] || 'destination'
          const validStages = ruleType.available_until_states.map(
            (s) => s.label,
          )

          return (
            <ChangeRuleSlider
              key={ruleType.key}
              category={category}
              label={ruleType.descriptor}
              masterStages={masterStages}
              validStages={validStages}
              autoAcceptUntil={ruleState.autoAcceptUntil as UntilStage}
              manualApprovalUntil={ruleState.manualApprovalUntil as UntilStage}
              onAutoAcceptChange={(stage) =>
                handleAutoAcceptChange(ruleType.key, stage)
              }
              onManualApprovalChange={(stage) =>
                handleManualApprovalChange(ruleType.key, stage)
              }
              autoAcceptDisabled={autoAcceptDisabled}
            />
          )
        })
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          paddingBottom: 16,
        }}
      >
        <LegendItem color={ZONE_COLORS.autoAccept} label='Auto-Accept' />
        <LegendItem color={ZONE_COLORS.manual} label='Manual Approval' />
        <LegendItem color={ZONE_COLORS.autoReject} label='Auto-Reject' />
      </div>
    </Modal>
  )
}
